import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

export interface Bankverbindung {
  bankverbindung_id: number;
  bank_name: string;
  iban: string;
  bic: string | null;
  reihenfolge: number;
  ist_primaer: boolean;
}

interface Props {
  onFehler: (m: string | null) => void;
}

const LEER_FORM = { bank_name: '', iban: '', bic: '' };

/**
 * Bankverbindungen-Reiter der Firma-Sektion (`/api/firma/bankverbindungen`,
 * Task 3-API). Die primäre Verbindung liefert IBAN/BIC für die E-Rechnung
 * (BT-84/BT-86). Das Backend erzwingt genau eine primäre Verbindung und
 * lässt sich nur "umstellen" — `PUT .../{id}` ignoriert `ist_primaer: false`,
 * ein Demoten ohne eine andere Verbindung zu befördern ist nicht vorgesehen.
 * Das Radio-Feld bildet das exakt ab: Auswahl = Umstellen, kein Abwählen.
 */
const FirmaBankverbindungen: React.FC<Props> = ({ onFehler }) => {
  const [banken, setBanken] = useState<Bankverbindung[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(LEER_FORM);

  const load = useCallback(() => {
    setLoading(true);
    axios.get<Bankverbindung[]>(`${API_URL}/api/firma/bankverbindungen`)
      .then(r => setBanken(r.data))
      .catch((err: unknown) => {
        const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
        onFehler(`Bankverbindungen konnten nicht geladen werden: ${msg}`);
      })
      .finally(() => setLoading(false));
  }, [onFehler]);

  useEffect(() => { load(); }, [load]);

  const anfrage = async (fn: () => Promise<unknown>, fehlertext: string) => {
    onFehler(null);
    setSaving(true);
    try {
      await fn();
      load();
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : 'Unbekannter Fehler';
      onFehler(`${fehlertext}: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const anlegen = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.bank_name.trim() || !form.iban.trim()) {
      onFehler('Bank und IBAN sind erforderlich.');
      return;
    }
    anfrage(async () => {
      await axios.post(`${API_URL}/api/firma/bankverbindungen`, {
        bank_name: form.bank_name.trim(),
        iban: form.iban.trim(),
        bic: form.bic.trim() || null,
      });
      setForm(LEER_FORM);
    }, 'Bankverbindung konnte nicht angelegt werden');
  };

  const loeschen = (b: Bankverbindung) => {
    // Das Backend befoerdert beim Loeschen der primaeren Verbindung nur dann
    // eine andere, wenn ueberhaupt noch eine uebrig bleibt (`if rest:` in
    // delete_bankverbindung). Bei der letzten Verbindung bliebe sonst eine
    // Warnung stehen, die genau das Gegenteil dessen verspricht, was passiert.
    let zusatz = '';
    if (b.ist_primaer) {
      zusatz = banken.length > 1
        ? '\n\nSie ist aktuell die primäre Verbindung. Beim Löschen rückt automatisch eine verbleibende Verbindung nach.'
        : '\n\nSie ist die letzte hinterlegte Verbindung. Danach ist keine Bankverbindung mehr vorhanden — für die E-Rechnung (ZUGFeRD/XRechnung) ist eine IBAN aber Pflicht.';
    }
    if (!window.confirm(`Bankverbindung "${b.bank_name}" (${b.iban}) löschen?${zusatz}`)) return;
    anfrage(
      () => axios.delete(`${API_URL}/api/firma/bankverbindungen/${b.bankverbindung_id}`),
      'Löschen fehlgeschlagen',
    );
  };

  const primaerSetzen = (b: Bankverbindung) => {
    if (b.ist_primaer) return;
    anfrage(
      () => axios.put(`${API_URL}/api/firma/bankverbindungen/${b.bankverbindung_id}`, { ist_primaer: true }),
      'Umstellen fehlgeschlagen',
    );
  };

  return (
    <div>
      <p style={{ color: 'var(--ink-2)', marginBottom: 16, fontSize: 14 }}>
        Die primäre Bankverbindung erscheint auf Rechnungen und ist die IBAN/BIC-Quelle für die
        E-Rechnung (ZUGFeRD/XRechnung). Zum Ändern eine andere Verbindung als primär auswählen —
        es muss immer genau eine primäre Verbindung geben, daher lässt sich die aktuelle nicht
        einfach abwählen.
      </p>

      {loading ? (
        <div>Lade Bankverbindungen…</div>
      ) : (
        <div className="table-container">
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 10px 6px 0' }}>Bank</th>
                <th style={{ textAlign: 'left', padding: '6px 10px' }}>IBAN</th>
                <th style={{ textAlign: 'left', padding: '6px 10px' }}>BIC</th>
                <th style={{ textAlign: 'center', padding: '6px 10px' }}>Primär</th>
                <th style={{ textAlign: 'right', padding: '6px 0' }}></th>
              </tr>
            </thead>
            <tbody>
              {banken.map(b => (
                <tr key={b.bankverbindung_id} style={{ borderBottom: '1px solid var(--bone-3)' }}>
                  <td style={{ padding: '8px 10px 8px 0', fontWeight: b.ist_primaer ? 600 : 400 }}>
                    {b.bank_name}
                    {b.ist_primaer && (
                      <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--green-500)' }}>
                        Primär
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', fontFamily: 'var(--font-mono)', fontSize: 13 }}>{b.iban}</td>
                  <td style={{ padding: '8px 10px' }}>{b.bic ?? '—'}</td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                    <input
                      type="radio"
                      name="firma-primaer-bank"
                      checked={b.ist_primaer}
                      onChange={() => primaerSetzen(b)}
                      disabled={saving}
                      aria-label={`${b.bank_name} als primäre Bankverbindung festlegen`}
                    />
                  </td>
                  <td style={{ padding: '8px 0', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button type="button" className="k-btn k-btn--danger k-btn--sm" disabled={saving} onClick={() => loeschen(b)}>
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
              {banken.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--ink-2)' }}>
                    Noch keine Bankverbindung hinterlegt.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <form onSubmit={anlegen} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          Bank
          <input
            value={form.bank_name}
            onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))}
            placeholder="z.B. Sparkasse"
            required
            style={{ padding: '7px 10px', minWidth: 180 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          IBAN
          <input
            value={form.iban}
            onChange={e => setForm(f => ({ ...f, iban: e.target.value }))}
            placeholder="DE…"
            required
            style={{ padding: '7px 10px', minWidth: 220 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          BIC
          <input
            value={form.bic}
            onChange={e => setForm(f => ({ ...f, bic: e.target.value }))}
            placeholder="optional"
            style={{ padding: '7px 10px', minWidth: 140 }}
          />
        </label>
        <button type="submit" className="k-btn k-btn--primary" disabled={saving}>
          + Hinzufügen
        </button>
      </form>
    </div>
  );
};

export default FirmaBankverbindungen;
